import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
  AveragePlayerScore,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { HeadlineM, LabelS } from '@sorare/core/src/atoms/typography';
import { BonusChip } from '@sorare/core/src/components/card/BonusChip';
import { FlagAvatar } from '@sorare/core/src/components/country/FlagAvatar';
import { PlayerAppearance } from '@sorare/core/src/components/scoring/PlayerAppearance';
import { PlayerScore } from '@sorare/core/src/components/scoring/PlayerScore';
import { cardAttributes, glossary } from '@sorare/core/src/lib/glossary';

import { AnyPlayerStatsIcon } from 'components/player/AnyPlayerStatsIcon';

import { PlayerRotowireStatsProjection } from './PlayerRotowireStatsProjection';
import type {
  PlayerStats_anyCardInterface,
  PlayerStats_anyPlayerInterface,
  PlayerStats_playerGameScoreInterface,
} from './__generated__/index.graphql';

const Root = styled(SBHorizontal)`
  align-items: stretch;

  > ${Vertical} {
    justify-content: space-between;
  }
`;

type Props = {
  anyPlayer: PlayerStats_anyPlayerInterface;
  anyCard?: PlayerStats_anyCardInterface;
  eligiblePlayerGameScores?: PlayerStats_playerGameScoreInterface[];
};

export const PlayerStats = ({
  anyPlayer,
  anyCard,
  eligiblePlayerGameScores = [],
}: Props) => {
  return (
    <Root>
      {anyPlayer.sport === Sport.FOOTBALL && (
        <>
          <Vertical gap={0.5}>
            <LabelS bold color="var(--c-nd-600)">
              <FormattedMessage {...cardAttributes.last5} />
            </LabelS>
            <Horizontal>
              <HeadlineM>
                <PlayerScore
                  animated={false}
                  shape="TEXT"
                  player={anyPlayer}
                  mode={AveragePlayerScore.LAST_FIVE_SO5_AVERAGE_SCORE}
                />
              </HeadlineM>
              <AnyPlayerStatsIcon player={anyPlayer} />
              <PlayerAppearance
                player={anyPlayer}
                mode={AveragePlayerScore.LAST_FIVE_SO5_AVERAGE_SCORE}
              />
            </Horizontal>
          </Vertical>
          <Vertical gap={0.5}>
            <LabelS bold color="var(--c-nd-600)">
              <FormattedMessage {...cardAttributes.last15} />
            </LabelS>
            <Horizontal>
              <HeadlineM>
                <PlayerScore
                  animated={false}
                  shape="TEXT"
                  player={anyPlayer}
                  mode={AveragePlayerScore.LAST_FIFTEEN_SO5_AVERAGE_SCORE}
                />
              </HeadlineM>
              <PlayerAppearance
                player={anyPlayer}
                mode={AveragePlayerScore.LAST_FIFTEEN_SO5_AVERAGE_SCORE}
              />
            </Horizontal>
          </Vertical>
          <Vertical gap={0.5}>
            <LabelS bold color="var(--c-nd-600)">
              <FormattedMessage {...cardAttributes.last40} />
            </LabelS>
            <Horizontal>
              <HeadlineM>
                <PlayerScore
                  animated={false}
                  shape="TEXT"
                  player={anyPlayer}
                  mode={AveragePlayerScore.LAST_FORTY_SO5_AVERAGE_SCORE}
                />
              </HeadlineM>
              <PlayerAppearance
                player={anyPlayer}
                mode={AveragePlayerScore.LAST_FORTY_SO5_AVERAGE_SCORE}
              />
            </Horizontal>
          </Vertical>
        </>
      )}
      {anyPlayer.sport === Sport.NBA && (
        <>
          <Vertical gap={0.5}>
            <LabelS bold color="var(--c-nd-600)">
              <FormattedMessage {...cardAttributes.last10} />
            </LabelS>
            <Horizontal>
              <HeadlineM>
                <PlayerScore
                  animated={false}
                  shape="TEXT"
                  player={anyPlayer}
                  mode={AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE}
                />
              </HeadlineM>
              <AnyPlayerStatsIcon player={anyPlayer} />
              <PlayerAppearance
                player={anyPlayer}
                mode={AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE}
              />
            </Horizontal>
          </Vertical>
          {!!eligiblePlayerGameScores.length && (
            <Vertical gap={0.5}>
              <LabelS bold color="var(--c-nd-600)">
                <FormattedMessage {...glossary.projection} />
              </LabelS>
              <div>
                <PlayerRotowireStatsProjection
                  player={anyPlayer}
                  positions={anyCard?.anyPositions || anyPlayer.anyPositions}
                  playerGameScores={eligiblePlayerGameScores}
                  variant="chip"
                />
              </div>
            </Vertical>
          )}
        </>
      )}
      {anyPlayer.sport === Sport.BASEBALL && (
        <>
          <Vertical gap={0.5}>
            <LabelS bold color="var(--c-nd-600)">
              <FormattedMessage {...cardAttributes.age} />
            </LabelS>
            <HeadlineM>{anyPlayer.age}</HeadlineM>
          </Vertical>
          <Vertical gap={0.5}>
            <LabelS bold color="var(--c-nd-600)">
              <FormattedMessage {...cardAttributes.country} />
            </LabelS>

            <FlagAvatar
              country={anyPlayer.country}
              type="flat"
              size={28}
              withTooltip
            />
          </Vertical>
        </>
      )}
      {!!anyCard && (
        <Vertical gap={0.5}>
          <LabelS bold color="var(--c-nd-600)">
            <FormattedMessage {...cardAttributes.bonus} />
          </LabelS>
          <BonusChip
            bonus={Number(anyCard.bonus)}
            powerBreakdown={anyCard.powerBreakdown}
          />
        </Vertical>
      )}
    </Root>
  );
};

PlayerStats.fragments = {
  anyPlayerInterface: gql`
    fragment PlayerStats_anyPlayerInterface on AnyPlayerInterface {
      slug
      sport
      age
      anyPositions
      country {
        slug
        name
      }
      ...PlayerScore_anyPlayer
      ...AnyPlayerStatsIcon_anyPlayer
      ...PlayerAppearance_anyPlayer
      ...PlayerRotowireStatsProjection_AnyPlayerInterface
    }
    ${PlayerScore.fragments.anyPlayer}
    ${AnyPlayerStatsIcon.fragments.anyPlayer}
    ${PlayerAppearance.fragments.anyPlayer}
    ${PlayerRotowireStatsProjection.fragments.anyPlayerInterface}
  ` as TypedDocumentNode<PlayerStats_anyPlayerInterface>,
  anyCardInterface: gql`
    fragment PlayerStats_anyCardInterface on AnyCardInterface {
      slug
      anyPositions
      bonus: power(so5LeaderboardSlug: $so5LeaderboardSlug)
      powerBreakdown(so5LeaderboardSlug: $so5LeaderboardSlug) {
        ...BonusChip_powerBreakdown
      }
    }
    ${BonusChip.fragments.powerBreakdown}
  ` as TypedDocumentNode<PlayerStats_anyCardInterface>,
  playerGameScoreInterface: gql`
    fragment PlayerStats_playerGameScoreInterface on PlayerGameScoreInterface {
      id
      ...PlayerRotowireStatsProjection_PlayerGameScoreInterface
    }
    ${PlayerRotowireStatsProjection.fragments.playerGameScoreInterface}
  ` as TypedDocumentNode<PlayerStats_playerGameScoreInterface>,
};
